import { Check } from '../../../../modules/design-system';
import type { PoolShippingStatus } from '../../../../modules/api';

export interface ShipmentStepperProps {
  status: PoolShippingStatus;
}

interface Step {
  key: string;
  label: string;
  /** Secondary caption under the label — context about that stage, shown
   *  while it's current or upcoming and dropped once the step is passed. */
  hint?: string;
}

const STEPS: Step[] = [
  { key: 'pending', label: 'Request created' },
  { key: 'paid', label: 'Paid' },
  {
    key: 'request_shipping',
    label: 'Processing at the vault',
    hint: 'Processing may take up to 10 business days',
  },
  { key: 'shipped', label: 'Shipped' },
  // Reached once the carrier reports the parcel delivered (EasyPost tracker);
  // until then it stays as the dimmed upcoming step.
  { key: 'delivered', label: 'Delivered' },
];

// Backend enum (LootMachine::PoolShipping#status) has 8 values; `merged` is
// filtered out server-side for every iframe endpoint (a shipment merged into
// another one is never returned here), so it's intentionally absent below —
// an unmapped status falls back to index 0 via `?? 0`.
// `waiting_for_payment` (invoice generated, pre-`paid`) has no step of its
// own and still reads as "Request created".
const STEP_INDEX: Record<string, number> = {
  pending: 0,
  waiting_for_payment: 0,
  paid: 1,
  request_shipping: 2,
  shipped: 3,
  delivered: 4,
};

const TRACK_COLOR = 'rgba(255,255,255,0.14)';
const FILL_COLOR = 'var(--ui-button)';

/**
 * 5-step fulfilment tracker on the Delivery Show page. The caller is
 * responsible for not rendering this when `status === 'canceled'` — a
 * cancellation can happen at any step, so it doesn't fit a linear progress.
 *
 * Equal grid columns keep the dot and its label on the same center no
 * matter the label's length. Each connector is an absolutely-positioned
 * line starting at its own column's center and spanning exactly one column
 * width, so it always lands precisely on the next dot's center — a
 * flex-sibling connector would instead start at the (label-widened) column
 * edge and leave a gap before reaching the dot.
 */
// Half of the dot's diameter (`size-5` = 20px) — insets the connector so it
// stops at each dot's edge instead of running underneath it. A connector
// reaching all the way to the next dot's center would sit *behind* the dot,
// which looks fine for an opaque done/active dot but bleeds visibly through
// an upcoming dot's translucent fill.
const DOT_RADIUS_PX = 10;

export function ShipmentStepper({ status }: ShipmentStepperProps) {
  const currentIndex = STEP_INDEX[status] ?? 0;
  const lastIndex = STEPS.length - 1;

  return (
    <ol className="grid grid-cols-5">
      {STEPS.map((step, index) => {
        const done = index <= currentIndex;
        return (
          <li key={step.key} className="relative flex flex-col items-center gap-2">
            {index < lastIndex && (
              <div
                className="absolute top-[9px] h-[2px] rounded-full"
                style={{
                  left: `calc(50% + ${DOT_RADIUS_PX}px)`,
                  width: `calc(100% - ${DOT_RADIUS_PX * 2}px)`,
                  background: index < currentIndex ? FILL_COLOR : TRACK_COLOR,
                }}
              />
            )}
            <StepDot done={done} />
            <span
              className="text-center text-[12px] leading-[16px]"
              style={{
                color: index <= currentIndex ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.45)',
              }}
            >
              {step.label}
            </span>
            {step.hint && index >= currentIndex && (
              <p
                className="text-center text-[11px] leading-[14px] italic"
                style={{ color: 'var(--co-ShippingItemStatus-refundedFg, #FFB020)' }}
              >
                {step.hint}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StepDot({ done }: { done: boolean }) {
  if (done) {
    return (
      <span
        className="inline-flex size-5 shrink-0 items-center justify-center rounded-full"
        style={{ background: FILL_COLOR, color: 'var(--ui-button-text, #fff)' }}
      >
        <Check size={12} strokeWidth={3} />
      </span>
    );
  }

  return (
    <span
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full"
      style={{ background: 'rgba(255,255,255,0.10)', border: `2px solid ${TRACK_COLOR}` }}
    />
  );
}
